import type { ComponentType } from 'react';

import { useAppSelector } from '../../store/react_redux_typed';
import type { ModalComponentProps } from './modals_manager/types';
import { getModalContextItems, selectOptions } from './selectors';

/**
 * See selectModalContextItems for why the store's items and options are not
 * the declared types. TypeScript also cannot prove that putting them back onto
 * `Omit<Props, ...>` gives `Props` for a generic `Props`.
 */
const asDeclaredProps = <Props,>(props: object): Props =>
  // biome-ignore lint/plugin: see above.
  props as Props;

/**
 * Reads a modal's context items and options out of the store as the types the
 * modal declared, so it no longer widens them at each use.
 */
const withContextItems = <Props extends ModalComponentProps>(
  Component: ComponentType<Props>,
): ComponentType<Omit<Props, 'item' | 'items' | 'options'>> => {
  function WithContextItems(props: Omit<Props, 'item' | 'items' | 'options'>) {
    const { modalId } = props;
    const items = useAppSelector((state) =>
      getModalContextItems(state, modalId),
    );
    const options = useAppSelector((state) => selectOptions(state, modalId));

    return (
      <Component
        {...asDeclaredProps<Props>({
          ...props,
          item: items[0],
          items,
          options,
        })}
      />
    );
  }
  WithContextItems.displayName = `withContextItems(${Component.displayName ?? Component.name})`;
  return WithContextItems;
};

export default withContextItems;
